
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { PlusCircle, ArrowUpDown, EuroIcon, CreditCard, Wallet } from 'lucide-react';
import { toast } from 'sonner';

// Wallet funding form component
const FundWalletForm = ({ onClose }: { onClose: () => void }) => {
  const [amount, setAmount] = useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    // Here you would typically process the payment
    toast.success(`Added €${amount} to bubble wallet`, {
      description: 'The funds are now available for care services',
    });
    
    onClose();
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label htmlFor="amount" className="block text-sm font-medium">
          Amount to add
        </label>
        <div className="relative">
          <EuroIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            id="amount"
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="pl-10 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="0.00"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <label htmlFor="payment-method" className="block text-sm font-medium">
          Payment Method
        </label>
        <div className="flex items-center space-x-2 border rounded-md p-3">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Credit Card ending in 4242</span>
        </div>
      </div>
      
      <div className="flex justify-end space-x-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button type="submit">Add Funds</Button>
      </div>
    </form>
  );
};

const BubbleWallet = () => {
  const [fundDialogOpen, setFundDialogOpen] = useState(false);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-tech-600" />
          Bubble Wallet
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center py-4">
          <div className="text-3xl font-bold">€1,250.00</div>
          <p className="text-sm text-muted-foreground mt-1">Available balance</p>
          
          <div className="w-full mt-6 space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Family contributions</span>
              <span className="font-medium">€950.00</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Volunteer credits</span>
              <span className="font-medium">€300.00</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Spent this month</span>
              <span className="font-medium">€430.00</span>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex gap-2">
        <Dialog open={fundDialogOpen} onOpenChange={setFundDialogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full">
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Funds
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Funds to Bubble Wallet</DialogTitle>
              <DialogDescription>
                Added funds can be used by caregivers for approved services
              </DialogDescription>
            </DialogHeader>
            <FundWalletForm onClose={() => setFundDialogOpen(false)} />
          </DialogContent>
        </Dialog>
        <Button variant="outline" className="w-full">
          <ArrowUpDown className="h-4 w-4 mr-2" />
          Transactions
        </Button>
      </CardFooter>
    </Card>
  );
};

export default BubbleWallet;
